import { Alert, Button, Checkbox, Divider, Form, Input } from 'antd';
import { useSignupMutation } from 'api/AuthApi';
import classNames from 'classnames';
import AuthComponent from 'components/AuthComponent/AuthComponent';
import { useCompanyContext } from 'contexts/CompanyContext';
import SocialLogin from 'pages/Auth/SocialLogin/SocialLogin';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { removeAffiliateCode, selectAffiliateCode } from 'store/affiliateSlice';
import { useAppDispatch } from 'store/store';
import { getInviteCodeFromSearchParams } from 'utils/brandedInviteSignup';
import { logging, validateForm } from 'utils/utils';
import './SignUp.css';

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
const ORGANIZATION_CODE_REQUIRED_ERROR = 'Organization code is required';
const ORGANIZATION_CODE_INVALID_ERROR = 'Invalid organization code';

const getEmailFromQuery = (queryEmail: string | null) => {
  if (!queryEmail) {
    return '';
  }

  try {
    const decodedEmail = decodeURIComponent(queryEmail);
    return isValidEmail(decodedEmail) ? decodedEmail : '';
  } catch {
    return '';
  }
};

const getSignUpInitialValues = (searchParams: URLSearchParams, isInviteBasedSignup: boolean) => ({
  email: getEmailFromQuery(searchParams.get('email')),
  firstName: searchParams.get('firstName') ?? searchParams.get('firstname') ?? '',
  lastName: searchParams.get('lastName') ?? searchParams.get('lastname') ?? '',
  organizationCode: isInviteBasedSignup ? '' : searchParams.get('organizationCode') ?? searchParams.get('orgCode') ?? ''
});

const hasInviteSignupContext = (searchParams: URLSearchParams) => Boolean(getInviteCodeFromSearchParams(searchParams));

const getOrganizationCodeErrorMessage = (error: unknown) => {
  const response = error as {
    data?: {
      message?: unknown;
      errors?: Array<{ path?: string; msg?: string }>;
    };
  };

  const fieldMessages = (response?.data?.errors || [])
    .filter((item) => item.path === 'organizationCode' && typeof item.msg === 'string')
    .map((item) => item.msg as string);

  const messages = [response?.data?.message, ...fieldMessages].filter((message): message is string => typeof message === 'string');

  return messages.find(
    (message) => message === ORGANIZATION_CODE_REQUIRED_ERROR || message === ORGANIZATION_CODE_INVALID_ERROR
  );
};

function SignUp() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { companyConfig } = useCompanyContext();
  const affiliateCode = useSelector(selectAffiliateCode);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isConfirmedError, setIsConfirmedError] = useState(false);
  const [signup, { isError, isLoading }] = useSignupMutation();
  const [form] = Form.useForm();
  const inviteCode = getInviteCodeFromSearchParams(searchParams);
  const isInviteBasedSignup = hasInviteSignupContext(searchParams);
  const initialValues = getSignUpInitialValues(searchParams, isInviteBasedSignup);
  const shouldRequireOrganizationCode = companyConfig.isEnterprise && !isInviteBasedSignup;

  const onSubmit = async () => {
    try {
      await form.validateFields();
      const fields = form.getFieldsValue();

      setIsConfirmedError(false);
      if (!isConfirmed) {
        setIsConfirmedError(true);
        return;
      }

      await signup({
        firstName: fields.firstName?.trim(),
        lastName: fields.lastName?.trim(),
        email: fields.email,
        password: fields.password,
        code: inviteCode,
        organizationCode: shouldRequireOrganizationCode ? fields.organizationCode?.trim() || undefined : undefined,
        partnerCode: affiliateCode,
        origin: window.location.origin
      }).unwrap();

      dispatch(removeAffiliateCode());

      form.setFieldValue('firstName', '');
      form.setFieldValue('lastName', '');
      form.setFieldValue('organizationCode', '');
      form.setFieldValue('email', '');
      form.setFieldValue('password', '');

      navigate(`/confirm-email?email=${encodeURIComponent(fields.email)}`);
    } catch (e) {
      logging(e);

      const organizationCodeErrorMessage = getOrganizationCodeErrorMessage(e);
      if (organizationCodeErrorMessage) {
        form.setFields([
          {
            name: 'organizationCode',
            errors: [organizationCodeErrorMessage]
          }
        ]);
      }

      validateForm(form, e);
    }
  };

  return (
    <AuthComponent
      title="Sign Up"
      subtitle={
        <>
          Already have an account? <Link to="/login">Login Here</Link>
        </>
      }
    >
      <div className="signup">
        <Divider plain>or</Divider>

        <SocialLogin />

        <Divider plain>or</Divider>

        <Form
          name="basic"
          layout="vertical"
          onFinish={onSubmit}
          autoComplete="off"
          requiredMark="optional"
          form={form}
          initialValues={initialValues}
        >
          {isError && <Alert className="message" message="Cannot create an account" type="error" showIcon />}

          <Form.Item label="First Name" name="firstName" validateTrigger="onBlur" rules={[{ required: true, whitespace: true }]}>
            <Input size="large" autoComplete="given-name" />
          </Form.Item>

          <Form.Item label="Last Name" name="lastName" validateTrigger="onBlur" rules={[{ required: true, whitespace: true }]}>
            <Input size="large" autoComplete="family-name" />
          </Form.Item>

          {shouldRequireOrganizationCode && (
            <Form.Item
              label="Organization Code"
              name="organizationCode"
              validateTrigger="onBlur"
              extra="Enter the organization code provided by your organization."
              rules={[{ required: true, whitespace: true, message: ORGANIZATION_CODE_REQUIRED_ERROR }]}
            >
              <Input size="large" autoComplete="organization" />
            </Form.Item>
          )}

          <Form.Item label="Email" name="email" validateTrigger="onBlur" rules={[{ required: true }, { type: 'email' }]}>
            <Input size="large" placeholder="email@domain.com" />
          </Form.Item>

          <Form.Item
            label="Password"
            name="password"
            rules={[{ required: true }, { min: 8, message: 'Password must be at least 8 characters' }]}
          >
            <Input.Password size="large" autoComplete="new-password" />
          </Form.Item>

          <div className="agree-block">
            <Checkbox
              className={classNames({
                error: isConfirmedError
              })}
              onChange={(e) => {
                setIsConfirmed(e.target.checked);
                setIsConfirmedError(false);
              }}
            >
              By checking this box, you certify that you are at least 18 years old and agree to our{' '}
              <Link to={companyConfig.termsUrl} target="_blank" rel="noopener noreferrer">
                Terms
              </Link>{' '}
              and{' '}
              <Link to={companyConfig.privacyUrl} target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </Link>
            </Checkbox>
          </div>

          <Form.Item>
            <Button block type="primary" htmlType="submit" loading={isLoading}>
              Sign Up
            </Button>
          </Form.Item>
        </Form>
      </div>
    </AuthComponent>
  );
}

export default SignUp;
